type Fbq = ((...args: unknown[]) => void) & { callMethod?: unknown };

const fbeventsUrl = 'https://connect.facebook.net/en_US/fbevents.js';

// Meta's base code, guarded so it only ever runs init / PageView once per page.
function getBaseCode(id: string): string {
    return `if(!window.fbq){!function(f,b,e,v,n,t,s){n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,document,'script','${fbeventsUrl}');fbq('init',${JSON.stringify(id)});fbq('track','PageView');}`;
}

export function useMetaPixel() {
    const nuxtApp = useNuxtApp();
    const {
        isMetaFbPixelEnabled,
        metaFbPixelId
    } = useRuntimeConfig().public;
    const isEnabled = Boolean(isMetaFbPixelEnabled && metaFbPixelId);

    function getFbq(): Undefinable<Fbq> {
        return import.meta.client ? (globalThis as { fbq?: Fbq }).fbq : undefined;
    }

    // Rendered into the server HTML when called during SSR, so PageView does
    // not wait for hydration. The key keeps the client from adding it twice.
    function load(): void {
        if (!isEnabled) return;

        nuxtApp.runWithContext(() => useHead({
            script: [{
                key: 'meta-pixel',
                innerHTML: getBaseCode(String(metaFbPixelId))
            }]
        }));
    }

    // fbevents.js replaces the stub's queueing with `fbq.callMethod`. A consent
    // command queued before then makes fbevents stop draining its queue, so
    // init / PageView behind it never fire.
    function isLoaded(): boolean {
        return typeof getFbq()?.callMethod === 'function';
    }

    function grant(): void {
        if (isLoaded()) {
            getFbq()?.('consent', 'grant');
        } else {
            load();
        }
    }

    function revoke(): void {
        if (isLoaded()) {
            getFbq()?.('consent', 'revoke');
        }
    }

    function track(event: string, params: object = {}): void {
        getFbq()?.('track', event, params);
    }

    return {
        isEnabled,
        load,
        grant,
        revoke,
        track
    };
}
